import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import type { Address, DropdownSelectOption } from '../types/types';
import { countryList } from '../const/const';
import DropdownSelect from './common/DropdownSelect';

interface EditAddressModalProps {
  show: boolean;
  onHide: () => void;
  address: Address;
  loading?: boolean;
  onSave: (updatedAddress: Address) => void;
}

const EditAddressModal: React.FC<EditAddressModalProps> = ({
  show,
  onHide,
  address,
  loading = false,
  onSave,
}) => {
  const [editedAddress, setEditedAddress] = useState<Address>(address);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );

  useEffect(() => {
    setEditedAddress(address); // reset when modal opens with new address
    setErrors({});
  }, [address, show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedAddress((prev) => ({ ...prev, [name]: value }));
    if (value.trim() !== '') {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!editedAddress?.street?.trim()) newErrors.street = 'Street is required';
    if (!editedAddress?.city?.trim()) newErrors.city = 'City is required';
    if (!editedAddress?.state?.trim()) newErrors.state = 'State is required';
    if (!editedAddress?.postal_code?.trim())
      newErrors.postal_code = 'Postal code is required';
    if (!editedAddress?.country?.trim())
      newErrors.country = 'Country is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(editedAddress);
    }
  };

  // Map Country objects to Option type
  const countryOptions: DropdownSelectOption[] = countryList.map((c) => ({
    id: c.isoCode,
    label: c.countryName,
  }));

  const handleCountrySelected = (
    option: DropdownSelectOption | DropdownSelectOption[]
  ) => {
    const opt = option as DropdownSelectOption;
    setEditedAddress((prev) => ({ ...prev, country: opt.id }));
    if (opt.id) {
      setErrors((prev) => ({ ...prev, country: undefined }));
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3 position-relative">
            <Form.Label>Country</Form.Label>
            <DropdownSelect
              options={countryOptions}
              selectedIds={editedAddress?.country || ''}
              onValueChanged={handleCountrySelected}
              isInvalid={!!errors.country} // will show red border if true
            />
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Street</Form.Label>
            <Form.Control
              type="text"
              name="street"
              value={editedAddress?.street}
              onChange={handleChange}
              isInvalid={!!errors.street}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.street}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>City</Form.Label>
            <Form.Control
              type="text"
              name="city"
              value={editedAddress?.city}
              onChange={handleChange}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.city}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>State</Form.Label>
            <Form.Control
              type="text"
              name="state"
              value={editedAddress?.state ?? ''}
              onChange={handleChange}
              isInvalid={!!errors.state}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.state}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Postal Code</Form.Label>
            <Form.Control
              type="text"
              name="postal_code"
              value={editedAddress?.postal_code}
              onChange={handleChange}
              isInvalid={!!errors.postal_code}
            />
            <Form.Control.Feedback tooltip type="invalid">
              {errors.postal_code}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAddressModal;
